import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { format } from "date-fns";
import moment from "moment";
import { CALL_DETAIL_COLUMNS } from "./columns";

import { downloadCSV } from "../utils";
import { formatDate } from "../../../helpers/date";

import CustomerBalanceSummaryFilters from "./CustomerBalanceSummaryFilters";
import CustomerBalanceSummaryList from "./CustomerBalanceSummaryList";
import * as codeActions from "../../../actions/codeActions";
import * as reportActions from "../../../actions/reportActions";

const CustomerBalanceSummaryPage = ({ codes, actions }) => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    page: 0,
    pageSize: 100,
    filtered: {
      end_date: format(new Date(), "yyyy-MM-dd")
    },
    sorted: []
  });
  const [data, setData] = useState([]);
  const [pageState, setPageState] = useState(1);

  useEffect(() => {
    if (!codes.length) {
      actions.loadCodes();
    }
  }, [actions, codes]);

  const fetchData = async (pageSize, page) => {
    setLoading(true);
    const { data: contracts, pages } =
      await actions.getCustomerBalanceSummaryReport({
        filtered: filters.filtered,
        isApproval: true,
        page: typeof page === "number" ? page : filters.page,
        pageSize: typeof pageSize === "number" ? pageSize : filters.pageSize,
        sorted: filters.sorted
      });

    setData(contracts);
    setPageState(pages);
    setLoading(false);
  };

  const handleChange = (name) => (value) => {
    setLoading(false);
    if (Array.isArray(value) && name === "customer") {
      setFilters((prev) => ({
        ...prev,
        filtered: { ...prev.filtered, [name]: value.map((v) => v.value) }
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        filtered: { ...prev.filtered, [name]: value }
      }));
    }
  };

  const handlePageSizeChange = (pageSize) => {
    localStorage.setItem("DEFAULT_PAGE_SIZE_CALL_LIST", pageSize);
    setFilters((prev) => ({ ...prev, pageSize }));
    fetchData(parseInt(localStorage.getItem("DEFAULT_PAGE_SIZE_CALL_LIST")));
  };

  const handlePageChange = async (page) => {
    setFilters((prev) => ({ ...prev, page }));
    fetchData(filters.pageSize, page);
  };

  const handleExport = async () => {
    const { data: contracts } = await actions.getCustomerBalanceSummaryReport({
      filtered: filters.filtered,
      isApproval: true,
      page: filters.page,
      pageSize: pageState * filters.pageSize,
      sorted: filters.sorted
    });

    const title = `Customer Balance Summary Through ${moment(
      filters.filtered.end_date
    ).format("MM/DD/YYYY")}`;

    downloadCSV(
      `CustomerBalanceSummary-${formatDate(new Date(), "yyyyMMdd")}`,
      contracts,
      CALL_DETAIL_COLUMNS,
      title
    );
  };

  const handleResetFilters = () => {
    setFilters({
      page: 0,
      pageSize: 100,
      filtered: {
        end_date: format(new Date(), "yyyy-MM-dd")
      },
      sorted: []
    });
    setData([]);
  };

  return (
    <div id="call-detail-page" className="report-page">
      <CustomerBalanceSummaryFilters
        codes={codes}
        filters={filters.filtered}
        onChange={handleChange}
        fetchData={fetchData}
        handleResetFilters={handleResetFilters}
        onExportClick={handleExport}
        exportDisabled={!data?.length}
        loading={loading}
      />
      <CustomerBalanceSummaryList
        data={data}
        fetchData={fetchData}
        filtered={filters}
        loading={loading}
        pages={pageState}
        onPageSizeChange={handlePageSizeChange}
        onPageChange={handlePageChange}
        onSortedChange={() => null}
        page={filters.page}
        sorted={filters.sorted}
        pageSize={filters.pageSize}
        defaultPageSize={
          localStorage.getItem("DEFAULT_PAGE_SIZE_CALL_LIST")
            ? parseInt(localStorage.getItem("DEFAULT_PAGE_SIZE_CALL_LIST"))
            : 10
        }
      />
    </div>
  );
};

CustomerBalanceSummaryPage.propTypes = {
  codes: PropTypes.array,
  actions: PropTypes.object
};

const mapStatesToProps = (state) => ({
  codes: state.reducers.codes
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      ...codeActions,
      ...reportActions
    },
    dispatch
  )
});

export default connect(
  mapStatesToProps,
  mapDispatchToProps
)(CustomerBalanceSummaryPage);
